import '@/assets/styles/fonts.css'
import { customIcons } from '@/plugins/vuetify/customIcons.js'
import { createVuetify } from 'vuetify'
import { VBtn } from 'vuetify/components'
import 'vuetify/styles'
const BORDER_RADIUS = 'true'
const vicampoThemeRebrand = {
  dark: false,
  colors: {
    background: '#f4ece4',
    surface: '#FFFFFF',
    primary: '#8b2243',
    'light-primary': '#bb2654',
    'extra-light-primary': '#d06a89',
    secondary: '#222222',
    error: '#e81038',
    info: '#2D2D2D',
    success: '#4CAF50',
    warning: '#FB8C00',
    // Vicampo colors
    graphit: '#2D2D2D',
    winered: '#8b2243',
    grape: '#8B2243',
    'light-grape': '#BB2654',
    'soft-grape': '#D06A89',
    ochre: '#dac1a7',
    'light-ochre': '#e8d9ca',
    beige: '#e2dcd1',
    'light-beige': '#f4ece4',
    soil: '#DAC1A7',
    'light-soil': '#E8D9CA',
    'soft-soil': '#F4ECE4',
    lavender: '#d6c7d6',
    'light-lavender': '#d5d2ed',
    'soft-lavender': '#dfe0f0',
    white: '#ffffff',
    black: '#000000',
    chalkboard: '#2d2e2f',
    'winered-dark': '#850d39',
    clay: '#918689',
    gray: '#666666',
    'light-gray': '#999999',
    'cement-gray': '#cccccc',
    'ash-gray': '#eeeeee',
    'dirty-white': '#f9f9f9',
    creme: '#f9f7f4',
    'light-creme': '#c5b8ac',
    'form-warning': '#ee5b5b',
    'mam-blue': '#0c2058',
    'great-gold': '#ebc03f',
    gold: '#ebc586',
    silver: '#96a4a5',
    // new additional colors:
    'grey-blue': '#3e4e82',
    'light-grey-blue': '#a9b3d3',
    purple: '#682772',
    'light-purple': '#c29ac7',
    'warm-brown': '#964421',
    'light-warm-brown': '#d59767',
    teal: '#0090a5',
    'light-teal': '#a0cecf',
    rose: '#d465a2',
    'light-rose': '#f9cfe3',
    tangerine: '#fe7a1d',
    'light-tangerine': '#fbc476',
    'warm-green': '#96aa24',
    'light-warm-green': '#dde470',
    magenta: '#e92d66',
    'light-magenta': '#f7b8ba',
    yellow: '#fdd30d',
    'light-yellow': '#ffebb1',
    elbwalker: '#7c3aed'
  }
}

const vicampoThemeHighContrast = {
  dark: false,
  colors: {
    background: '#f0ede6',
    surface: '#FFFFFF',
    primary: '#99003f',
    secondary: '#000000',
    error: '#EE5B5B',
    info: '#000000',
    success: '#4CAF50',
    warning: '#FB8C00',
    // Vicampo colors
    graphit: '#000000',
    winered: '#99003f',
    ochre: '#ae8a79',
    beige: '#e2dcd1',
    'light-beige': '#f0ede6',
    white: '#ffffff',
    black: '#000000',
    chalkboard: '#000000',
    'winered-dark': '#850d39',
    clay: '#000000',
    gray: '#000000',
    'light-gray': '#000000',
    'cement-gray': '#000000',
    'ash-gray': '#FFFFFF',
    'dirty-white': '#FFFFFF',
    creme: '#FFFFFF',
    'light-ochre': '#c5b8ac',
    'form-warning': '#ee5b5b',
    'mam-blue': '#0c2058',
    'great-gold': '#ebc03f',
    gold: '#ebc586',
    silver: '#96a4a5',
    elbwalker: '#7c3aed'
  }
}

export const getComponentDefaults = () => ({
  VCheckbox: {
    trueIcon: 'custom:checkboxOn',
    falseIcon: 'custom:checkboxOff'
  },
  VRadioGroup: {
    trueIcon: 'custom:radioButtonOn',
    falseIcon: 'custom:radioButtonOff'
  },
  VRadio: {
    trueIcon: 'custom:radioButtonOn',
    falseIcon: 'custom:radioButtonOff'
  },
  VBtn: {
    color: 'primary',
    variant: 'flat',
    rounded: BORDER_RADIUS
  },
  VBtnSecondary: {
    color: 'secondary',
    variant: 'outlined',
    rounded: BORDER_RADIUS
  },
  VRow: {
    noGutters: BORDER_RADIUS
  },
  VCard: {
    rounded: BORDER_RADIUS,
    elevation: 1
  },
  VSelect: {
    height: '56px',
    menuIcon: 'custom:down',
    variant: 'outlined',
    clearIcon: 'custom:close'
  },
  VTextField: {
    variant: 'outlined',
    rounded: BORDER_RADIUS,
    clearIcon: 'custom:close'
  },
  VTextarea: {
    variant: 'outlined',
    rounded: BORDER_RADIUS,
    clearIcon: 'custom:close'
  },
  VList: {
    rounded: BORDER_RADIUS
  },
  VExpansionPanels: {
    elevation: 0,
    rounded: BORDER_RADIUS,
    collapseIcon: 'custom:collapse',
    expandIcon: 'custom:expand'
  },
  VPagination: {
    variant: 'outlined',
    rounded: BORDER_RADIUS,
    prevIcon: 'custom:left',
    nextIcon: 'custom:right'
  },
  VDialog: {
    maxWidth: '1140px'
  },
  VSheet: {
    rounded: true,
    elevation: false,
    VSheet: {
      elevation: 2
    }
  },
  VSwitch: {
    color: 'primary'
  }
})

const opt = {
  // Skips browser measurements during setup so the server can render; the
  // client build keeps its current behavior (SSR is statically false there)
  ssr: import.meta.env.SSR,
  defaults: getComponentDefaults(),
  // Date formatting only — all three shops are German (de-DE/de-AT/de-CH), but
  // Vuetify's date adapter defaults to the UI locale ('en'), which renders the
  // date picker with English weekday labels starting on Sunday. Mapping the
  // active locale key to de-DE gives German labels and a Monday-first week
  // without switching Vuetify's other UI strings (VIP-12155).
  date: {
    locale: { en: 'de-DE' }
  },
  aliases: {
    VBtnSecondary: VBtn
  },
  theme: {
    defaultTheme: 'vicampoThemeRebrand',
    themes: {
      vicampoThemeHighContrast: vicampoThemeHighContrast,
      vicampoThemeRebrand: vicampoThemeRebrand
    }
  },
  icons: {
    sets: {
      custom: customIcons
    }
  }
}

// Vuetify holds reactive theme/display state, so the server entry creates a
// fresh instance per request (VIP-12037); a plain client-side navigation keeps
// the singleton below.
//
// The optional viewport ({ clientWidth, clientHeight }) seeds the display
// breakpoints. On the server it is the only width there is — without it
// Vuetify assumes 0 and every $vuetify.display v-if renders the xs branch, so
// desktop visitors watch the mobile layout until hydration patches it
// (VIP-12038).
//
// It is passed on the *client* too, when hydrating, and that is the whole
// point: Vuetify's `ssr` option makes the browser start from these values
// instead of window.innerWidth, so the hydration pass reproduces the server's
// breakpoint-dependent markup exactly. Vuetify then patches app.mount to call
// display.update() on the next tick, swapping in the real window size. Without
// it, every visitor whose viewport falls in a different Vuetify breakpoint
// than the guess — tablets, narrow laptop windows, anything 1920 and wider —
// got a hydration mismatch (VIP-12333).
export const createAppVuetify = (ssrViewport) =>
  createVuetify(ssrViewport ? { ...opt, ssr: ssrViewport } : opt)

const vuetify = createAppVuetify()

export default vuetify
